import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Loader from "./Loader";
import { teamWiseApis } from "../redux/team/teamActions";
import {
  selectTeamWise,
  selectTeamWiseStatus,
} from "../redux/team/teamSelectors";
import { getTeamColors } from "../utils/badgeColors";
import "../assets/css/teamPending.css";

/* The four pending states, in the order work moves through them. Complete and
   Not Possible are deliberately absent: this bar is about what is still
   outstanding, and finished work would swamp it (6,433 against 1,222). */
const PENDING_PARTS = [
  { key: "NotStarted_Nos", label: "Not Started", color: "#6c757d" },
  { key: "InProgress_Nos", label: "In Progress", color: "#0d6efd" },
  { key: "OnHold_Nos", label: "On Hold", color: "#f57c00" },
  { key: "Testing_Nos", label: "Testing", color: "#6f42c1" },
];

/* `onClick` turns the figure into a button and leaves it looking exactly the
   same. That is the point: it is one of three numbers in a row, and dressing
   one as a control would break the row to advertise itself. The pointer and
   the underline on hover are the affordance; the title says what it opens. */
const Stat = ({ label, value, color, bold, onClick, title }) => {
  const Wrapper = onClick ? "button" : "div";

  return (
  <Wrapper
    className={`text-center px-2${onClick ? " tw-pending-btn" : ""}`}
    {...(onClick
      ? { type: "button", onClick, title, disabled: !value }
      : {})}
  >
    <div
      className={onClick ? "tw-pending-btn__value" : undefined}
      style={{
        fontSize: bold ? "1.5rem" : "1.15rem",
        fontWeight: 700,
        color: color || "#212529",
        lineHeight: 1.1,
      }}
    >
      {value ?? 0}
    </div>
    <div
      className="text-muted"
      style={{ fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.4px" }}
    >
      {label}
    </div>
  </Wrapper>
  );
};

/* One team's card: the two headline numbers, then where the pending work is
   actually sitting. */
const TeamCard = ({ row, onOpenPending }) => {
  const colors = getTeamColors(row.TeamName);
  const pending = row.Pending_Nos || 0;

  return (
    <div
      className="content-card d-flex flex-column"
      style={{
        flex: "1 1 240px",
        minWidth: "240px",
        borderTop: `3px solid ${colors.text}`,
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span
          className="badge rounded-pill"
          style={{
            backgroundColor: colors.bg,
            color: colors.text,
            fontWeight: 700,
            fontSize: "0.78rem",
            padding: "6px 12px",
          }}
        >
          {row.TeamName}
        </span>
        <span className="text-muted" style={{ fontSize: "0.7rem" }}>
          {row.MemberCount} member{row.MemberCount === 1 ? "" : "s"}
        </span>
      </div>

      <div className="d-flex justify-content-around align-items-end mb-3">
        <Stat label="Total" value={row.Total_Nos} bold />
        <Stat
          label="Pending"
          value={pending}
          bold
          /* Zero pending is good news, so it reads green rather than as an
             alarm coloured number that happens to say 0. */
          color={pending > 0 ? "#d32f2f" : "#2e7d32"}
          /* The number was a dead end: it said 59 and left you to find them.
             Zero opens nothing — there is no list to show, and a control that
             opens an empty dialog is a control that wasted a click. */
          onClick={pending > 0 ? () => onOpenPending(row) : undefined}
          title={
            pending > 0
              ? `See the ${pending} pending task${pending === 1 ? "" : "s"} in ${row.TeamName}`
              : undefined
          }
        />
        <Stat label="Done" value={row.Complete_Nos} color="#2e7d32" />
      </div>

      {/* Proportional bar across the pending states. Rendered only when there
          is pending work — an empty grey strip says nothing. */}
      {pending > 0 && (
        <>
          <div
            className="d-flex mb-2"
            style={{
              height: "8px",
              borderRadius: "4px",
              overflow: "hidden",
              backgroundColor: "#f1f3f5",
            }}
          >
            {PENDING_PARTS.map((p) =>
              row[p.key] > 0 ? (
                <div
                  key={p.key}
                  title={`${p.label}: ${row[p.key]}`}
                  style={{
                    width: `${(row[p.key] / pending) * 100}%`,
                    backgroundColor: p.color,
                  }}
                />
              ) : null,
            )}
          </div>

          <div className="d-flex flex-wrap gap-2 mb-2">
            {PENDING_PARTS.filter((p) => row[p.key] > 0).map((p) => (
              <span
                key={p.key}
                className="d-inline-flex align-items-center gap-1 text-muted"
                style={{ fontSize: "0.7rem" }}
              >
                <i
                  className="bi bi-circle-fill"
                  style={{ fontSize: "0.45rem", color: p.color }}
                />
                {p.label} {row[p.key]}
              </span>
            ))}
          </div>
        </>
      )}

      <div className="mt-auto d-flex flex-wrap gap-3 pt-2 border-top">
        <span className="text-muted" style={{ fontSize: "0.7rem" }}>
          <i className="bi bi-check2-circle me-1" />
          {row.CompletionPercent}% done
        </span>

        {/* Only shown when it is true — a permanent "0 unclaimed" is noise. */}
        {row.Unassigned_Nos > 0 && (
          <span style={{ fontSize: "0.7rem", color: "#e65100", fontWeight: 600 }}>
            <i className="bi bi-person-dash me-1" />
            {row.Unassigned_Nos} unclaimed
          </span>
        )}

        {/* Work this team owns that is sitting with QA. On the Database card
            this is the 3 tasks that explain why the Testing card reads 1 —
            they were never moved to QC, only re-statused in place. */}
        {row.InQA_Nos > 0 && (
          <span style={{ fontSize: "0.7rem", color: "#6f42c1", fontWeight: 600 }}>
            <i className="bi bi-shield-check me-1" />
            {row.InQA_Nos} in QA
          </span>
        )}

        {row.OldestPendingDays > 0 && (
          <span
            style={{
              fontSize: "0.7rem",
              color: row.OldestPendingDays > 30 ? "#d32f2f" : "#6c757d",
              fontWeight: row.OldestPendingDays > 30 ? 600 : 400,
            }}
          >
            <i className="bi bi-clock-history me-1" />
            oldest {row.OldestPendingDays}d
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Team-wise workload: total and pending tasks per team.
 *
 * Deliberately all-time rather than filtered to the dashboard's date range.
 * The question this answers is "what is still open", and a date window would
 * hide the oldest stuck tasks — which are the ones worth looking at.
 */
const TeamWorkloadPanel = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { teams, grandTotal } = useSelector(selectTeamWise);
  const status = useSelector(selectTeamWiseStatus);

  /* The pending list is a screen of its own now, so which team is being looked
     at lives in the URL rather than in state here. Nothing to hold, nothing to
     leave stale, and the dashboard keeps its own tab — going back returns to
     this panel exactly as it was left.

     The names ride along so the heading is right on the first paint; the code
     is what the page actually resolves the team from. */
  const openPending = (row) => {
    const params = new URLSearchParams(
      row?.TeamCode == null
        ? { noTeam: "1" }
        : {
            teamCode: String(row.TeamCode),
            name: row.TeamName || "",
            short: row.ShortName || "",
          },
    );

    navigate(`/team-pending-tasks?${params.toString()}`);
  };

  useEffect(() => {
    dispatch(teamWiseApis({}));
  }, [dispatch]);

  if (status === "loading" && !teams.length) {
    return (
      <div className="content-card py-3">
        {/* inline — the panel is one card on a dashboard of cards, and a
            full-viewport skeleton for it hides everything that HAS loaded. */}
        <Loader isLoading inline variant="cards" rows={3} />
      </div>
    );
  }

  if (!teams.length) return null;

  const untagged = teams.find((t) => t.TeamName === "No Team");

  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
        <h5 style={{ color: "#333fad", margin: 0 }}>
          <i className="bi bi-diagram-3 me-2" />
          Team Workload
        </h5>

        {grandTotal && (
          <div className="d-flex gap-3 align-items-center">
            <span className="text-muted" style={{ fontSize: "0.78rem" }}>
              <strong style={{ color: "#212529" }}>
                {grandTotal.Total_Nos}
              </strong>{" "}
              total
            </span>
            <span className="text-muted" style={{ fontSize: "0.78rem" }}>
              <strong style={{ color: "#d32f2f" }}>
                {grandTotal.Pending_Nos}
              </strong>{" "}
              pending
            </span>
            <span className="text-muted" style={{ fontSize: "0.78rem" }}>
              <strong style={{ color: "#2e7d32" }}>
                {grandTotal.Complete_Nos}
              </strong>{" "}
              complete
            </span>

            {/* The QA queue: tasks sitting in a testing status, whoever owns
                them. Shown here because the Testing card counts by team, and
                a task can be with QA while the Database team still owns the
                record — which is why that card read 1 while the QA Tasks
                page listed 3. This is the number that page shows. */}
            {grandTotal.InQA_Nos > 0 && (
              <span
                className="text-muted"
                style={{ fontSize: "0.78rem" }}
                title="Tasks in a testing status, across all teams — what the QA Tasks page lists"
              >
                <strong style={{ color: "#6f42c1" }}>
                  {grandTotal.InQA_Nos}
                </strong>{" "}
                in QA
              </span>
            )}
          </div>
        )}
      </div>

      {/* Untagged work would otherwise be read as a team's backlog. It is the
          largest pending bucket by far, so it gets said out loud. */}
      {untagged && untagged.Pending_Nos > 0 && (
        <div className="alert alert-warning py-2 px-3 mb-2" style={{ fontSize: "0.8rem" }}>
          <i className="bi bi-exclamation-triangle me-1" />
          {/* The same number, opening the same list. This one is the largest
              pending figure on the panel and had the least to click. */}
          <button
            type="button"
            className="tw-pending-btn"
            onClick={() => openPending(untagged)}
            title={`See the ${untagged.Pending_Nos} tasks with no team`}
          >
            <strong className="tw-pending-btn__value">
              {untagged.Pending_Nos}
            </strong>
          </button>{" "}
          pending tasks have no team yet
          and are not counted against any team below. Set a team on the Task
          Assignment screen to bring them into these figures.
        </div>
      )}

      <div className="d-flex gap-3 flex-wrap align-items-stretch">
        {teams
          .filter((t) => t.TeamName !== "No Team")
          .map((row) => (
            <TeamCard key={row.TeamCode ?? row.TeamName} row={row} onOpenPending={openPending} />
          ))}
      </div>
    </div>
  );
};

export default TeamWorkloadPanel;
